
import {
    ICommandBarItemProps,
    Stack,
    TextField
} from '@fluentui/react';
import { RouterParams } from 'App';
import { RootState } from 'app/store';
import saveAs from 'file-saver';
import { emitCustomEvent } from 'global/Application';
import { IComparisonStateData, useApplication } from 'global/Application/Context';
import { SaveUnderwritingAction } from 'global/Application/dataActions';
import { useAuthentication } from 'global/Authentication';
import ComparisonEngine from 'global/Compare/ComparisonEngine';
import { ComparisonViewModelGenerator, IGroupConfigurationComparisonVM } from 'global/Compare/ComparisonViewModelGenerator';
import { IGCDataLight, updateSnapshotData } from 'global/Reducer/dataSlice';
import { setMerging, setProcessing } from 'global/Reducer/statusSlice';
import { useApplicationTheme } from 'global/themes';
import { GroupConfigurationSchemaValidator } from 'global/Validation/GroupConfigurationSchemaValidator';
import { bcbs_groupconfigurationsnapshot as IXrmOptions_bcbs_groupconfigurationsnapshot } from 'model/AttributeOptionSets.type';
import { ECommentStatus, IComment, ICommentRouteType } from 'model/FlexibleAttributeModel';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import GroupConfigurationSnapshotContext from 'views/BlueRelationship/GroupConfigurationSnapshotContext';
import CompareLookupDialog, { ECompareLookupDialogVariant, ICompareConfiguration, IDialogResult } from '../CompareLookupDialog/CompareLookupDialog';
import AddCommentDialog from './AddCommentDialog';
import { CONST_MARKER_OFF, CONST_MARKER_OFF_DARK, CONST_MARKER_ON, IMarkerSet } from './Compare.constants';
import {
    countPendingMergeSelections,
    EUnderwritingConfirmationDialogState,
    formatPendingLevelSummary,
    getLastLocation,
    getPendingLevelItems,
    getPendingMergeValidationDetails,
    ICommentInfo,
    IPendingMergeValidationError,
    LoadGroupConfigurationDataAsync,
    TSetCommentInfoDelegate,
} from './CompareContext.helpers';
import CompareFloatingActionButtons from './CompareFloatingActionButtons';
import CompareListSection from './CompareListSection';
import CustomPanel, { EPanelStyleType } from './CustomPanel';
import { buildMergedSnapshotJson } from './GroupConfigurationMergeBuilder';
import ModelWarningMessage from './ModelWarningMessage';
import PendingMergeWarning from './PendingMergeWarning';
import UnderwritingConfirmationDialog from './UnderwritingConfirmationDialog';

// re-exported so external imports from './CompareContext' keep working after the refactor
export { EUnderwritingConfirmationDialogState };
export type { ICommentInfo, TSetCommentInfoDelegate };

// Main Component
// ============================================================================
interface ICompareContextProps {
    isMergeApp?: boolean;
    isUnderwritingReviewApp?: boolean;
    isCompareApp?: boolean;
}

const CompareContext: React.FC<ICompareContextProps> = (props) => {
    const routeParams = useParams<RouterParams>();

    const authentication = useAuthentication();

    const state = useSelector((state: RootState) => state);
    const location = useLocation();

    const dispatch = useDispatch();
    const application = useApplication();

    const history = useHistory();

    const [isOpenComments, setCommentsPanel] = useState(false);
    const [isQuickNavOpen, setQuickNavOpen] = useState(false);

    const [compareConfiguration, setCompareConfiguration] = useState<ICompareConfiguration | null>(null);
    const [comparisonState, setComparisonState] = useState<IComparisonStateData | null>(null as any);
    const [isOpenCompareConfiguration, setIsOpenCompareConfiguration] = useState(true);
    // const [isOpenCompareExtractWizard, setIsOpenCompareExtractWizard] = useState(false);

    const [confirmationDialogState, setConfirmationDialogState] = useState<EUnderwritingConfirmationDialogState>(EUnderwritingConfirmationDialogState.Hidden);
    const [pendingMergeValidationError, setPendingMergeValidationError] = useState<IPendingMergeValidationError | null>(null);

    const [compareViewModel, setCompareViewModel] = useState<IGroupConfigurationComparisonVM | null>(null);

    const [commentInfo, setCommentInfo] = useState<ICommentInfo | null>(null);
    const [commentsLookup, setCommentsLookup] = useState<Record<string, IComment>>({});
    const [correctionComment, setCorrectionComment] = useState<string>('');

    const canUnderwritingReview = state.data.snapshot.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value
        || state.data.snapshot.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value

    // Ctrl+S / Cmd+S triggers the underwriting review save dialog
    useEffect(() => {
        if (!props.isUnderwritingReviewApp || !canUnderwritingReview)
            return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
                event.preventDefault();
                setConfirmationDialogState(EUnderwritingConfirmationDialogState.Save);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [props.isUnderwritingReviewApp, canUnderwritingReview]);

    useEffect(() => {
        // on mount, tell menu what to display
        let title = 'Compare';
        if (props.isUnderwritingReviewApp) {
            title = 'Underwriting Review';
        } else if (props.isMergeApp) {
            title = 'Merge';
        }

        document.title = `${title} | ${state.data?.snapshot?.bcbs_name ?? '-'}`;

    }, [state?.data?.snapshot])


    /**
     * helper function to run an async action with processing state and error handling.
     * This is used for actions that are triggered from the UI, such as extracting the comparison,
     * which can take some time and we want to show a loading state for it,
     * as well as catch any errors that may occur and show a global error message.
     */
    const runWithProcessing = useCallback(
        async (action: () => Promise<void>) => {
            try {
                dispatch(setProcessing(true));
                await action();
            } catch (error) {
                emitCustomEvent({
                    eventType: 'globalException',
                    details: error
                });
            } finally {
                dispatch(setProcessing(false));
            }
        },
        [dispatch]
    );

    /**
     * Extracts the comparison data based on the provided view model, and triggers a file download for the user.
     */
    const extractAsync = useCallback(async (viewModel: ICompareConfiguration) => {
        await (void runWithProcessing(async () => {

            const response = await application.dataSource.getSnapshotCompareExtract(
                {
                    bcbs_groupconfigurationsnapshotid: viewModel.leftSnapshotRef?.id!,
                    blobVersion: viewModel.leftBlobVersion ?? null
                },
                {
                    bcbs_groupconfigurationsnapshotid: viewModel.rightSnapshotRef?.id!,
                    blobVersion: viewModel.rightBlobVersion ?? null
                },
                viewModel.generateSingleSheet,
                viewModel.underwritingReviewNecessary,
                viewModel.affectsIDCards,
                viewModel.targetedClaims,
                viewModel.useReleaseID,

                () => {
                    // TODO implement a progress status
                    console.log(Date.now())
                },
            );

            saveAs(response.blob, response.fileName);
        }));
    }, [application.dataSource, runWithProcessing]);

    const appTheme = useApplicationTheme();

    const markers = useMemo<IMarkerSet>(() => ({
        MARKER_OFF: appTheme.theme === 'dark' ? CONST_MARKER_OFF : CONST_MARKER_OFF_DARK,
        MARKER_ON: CONST_MARKER_ON,
    }), [appTheme.theme]);


    const saveMergedSnapshotAsync = useCallback(async () => {
        if (!comparisonState || !comparisonState.origin || !comparisonState.target || !compareViewModel)
            return;

        const mergedJson = buildMergedSnapshotJson(
            comparisonState.target.json as any,
            compareViewModel,
        );

        dispatch(updateSnapshotData({
            json: mergedJson,
            snapshot: comparisonState.target.snapshot,
            config: comparisonState.target.config,
            account: comparisonState.target.account,
        }));

        history.push(`/records/blueRelationship/${comparisonState.target.account.accountid}/groupConfiguration/${comparisonState.target.snapshot.bcbs_groupconfigurationsnapshotid}`);
    }, [comparisonState, dispatch, history, compareViewModel]);

    const contextMenu = useMemo(() => {
        const menu: ICommandBarItemProps[] = [];

        // TODO we may want to have different options in the context menu depending on the view type, for example underwriting review may have options related to that, while compare app may have more general options. For now they will be the same, but we should keep this in mind as we develop both views.

        // submit for underwriting review should only show in underwriting review view, and should not show in compare app view
        // reject changes should only show in underwriting review view, and should not show in compare app view
        if (props.isUnderwritingReviewApp && canUnderwritingReview) {
            menu.push({
                key: 'merge.save',
                iconProps: { iconName: 'Send' },
                text: 'Save',
                disabled: false,
                subMenuProps: {
                    items: [
                        {
                            key: 'merge.save.save',
                            iconProps: { iconName: 'Save' },
                            text: 'Save',
                            disabled: false,
                            onClick: () => {
                                setConfirmationDialogState(EUnderwritingConfirmationDialogState.Save);
                            }
                        },
                        {
                            key: 'merge.save.approveChanges',
                            iconProps: { iconName: 'Accept' },
                            text: 'Approve Changes',
                            disabled: false,
                            onClick: () => {
                                setConfirmationDialogState(EUnderwritingConfirmationDialogState.Approve);
                            }
                        },
                        {
                            key: 'merge.save.returnChanges',
                            iconProps: { iconName: 'Cancel' },
                            text: 'Return for corrections',
                            disabled: false,
                            onClick: () => {
                                setConfirmationDialogState(EUnderwritingConfirmationDialogState.Corrections);
                            }
                        }
                    ]
                }
            });
        }

        if (props.isMergeApp) {
            menu.push({
                key: 'merge.preview',
                iconProps: { iconName: 'EntitlementRedemption' },
                text: 'Preview',
                disabled: !comparisonState || !compareViewModel,
                onClick: () => {
                    if (!compareViewModel)
                        return;

                    const unresolvedCount = countPendingMergeSelections(compareViewModel);
                    if (unresolvedCount > 0) {
                        const pendingDetails = getPendingMergeValidationDetails(compareViewModel);
                        const levelItems = getPendingLevelItems(pendingDetails.levelCounts);
                        const levelSummary = formatPendingLevelSummary(pendingDetails.levelCounts);

                        setPendingMergeValidationError({
                            summary: `Validation error: ${unresolvedCount} pending merge selection(s) remain.`,
                            levelItems: levelItems.length > 0
                                ? levelItems
                                : [levelSummary || 'Pending selections found'],
                            samples: pendingDetails.sampleLabels,
                        });
                        return;
                    }

                    setPendingMergeValidationError(null);

                    dispatch(setMerging(true));

                    runWithProcessing(async () => {
                        await saveMergedSnapshotAsync();
                    });
                }
            });
        }

        if (state.status.debug.admin) {
            menu.push({
                key: 'merge.validate',
                iconProps: { iconName: 'CheckMark' },
                text: 'Validate',
                onClick: () => {
                    const validator = new GroupConfigurationSchemaValidator();
                    const errors = validator.validate(structuredClone(state.data.json));
                    console.log(errors);
                }
            });
        }

        // show compare wizard option if user is in compare app view, or underwriting review
        if (!props.isMergeApp)
            menu.push({
                key: 'merge.compare',
                iconProps: { iconName: 'Compare' },
                text: 'Compare',
                disabled: false,
                onClick: () => {
                    setIsOpenCompareConfiguration(true);
                }
            });

        // show compare wizard option if user is in compare app view, or underwriting review
        if (props.isMergeApp)
            menu.push({
                key: 'merge.merge',
                iconProps: { iconName: 'MergeDuplicate' },
                text: 'Merge',
                disabled: false,
                onClick: () => {
                    setIsOpenCompareConfiguration(true);
                }
            });


        // compare extract menu
        menu.push({
            key: 'merge.extract',
            iconProps: { iconName: 'Download' },
            text: 'Extract',
            disabled: !compareConfiguration || !compareConfiguration.leftSnapshotRef || !compareConfiguration.rightSnapshotRef,
            onClick: () => {
                extractAsync(compareConfiguration!);
            }
        })

        // comments menu
        if (props.isUnderwritingReviewApp || state.status.debug.diagnosis)
            menu.push({
                key: 'merge.comments',
                iconProps: { iconName: 'Comment' },
                text: 'Comments',
                onClick: () => {
                    setCommentsPanel(true);
                }
            });

        return {
            items: menu,
            key: 'merge'
        };

    }, [
        props.isUnderwritingReviewApp,
        props.isMergeApp,
        state.data,
        authentication.authorizations?.roles,
        compareConfiguration,
        comparisonState,
        compareViewModel,
        runWithProcessing,
        saveMergedSnapshotAsync,
    ]);

    const updateQueryParams = useCallback((
        originState: IGCDataLight,
        targetState: IGCDataLight,
        _compareConfiguration: ICompareConfiguration,
    ) => {
        const searchParams = new URLSearchParams(location.search);

        const originId = originState.snapshot.bcbs_groupconfigurationsnapshotid;
        const targetId = targetState.snapshot.bcbs_groupconfigurationsnapshotid;

        if (originId) {
            searchParams.set('origin_bcbs_groupconfigurationsnapshotid', originId);
        } else {
            searchParams.delete('origin_bcbs_groupconfigurationsnapshotid');
        }

        if (targetId) {
            searchParams.set('target_bcbs_groupconfigurationsnapshotid', targetId);
        } else {
            searchParams.delete('target_bcbs_groupconfigurationsnapshotid');
        }

        history.replace({
            pathname: history.location.pathname,
            search: searchParams.toString(),
        });
    }, [history, location.search]);

    const onCompareConfigurationDialogContinue = useCallback(

        async (compareConfiguration: ICompareConfiguration) => {

            // ping for authentication before multiple parallel loads
            await application.dataSource.crm.whoAmI();

            setCompareViewModel(null);
            setPendingMergeValidationError(null);

            // navigate to compare view with selected parameters
            const result: IDialogResult = {
                dismiss: true,
            };

            const loadBlankConfiguration = () => {
                const data: IGCDataLight = {
                    json: {} as any,
                    snapshot: {} as any,
                    config: state.data.config,
                    account: structuredClone(state.data.account!),
                };

                return data;
            }

            const loadCurrentStateClone = () => {
                const data: IGCDataLight = {
                    json: structuredClone(state.data.json),
                    snapshot: structuredClone(state.data.snapshot),
                    config: state.data.config,
                    account: structuredClone(state.data.account!),
                };

                return data;
            }


            // Load selected snapshots from the dialog; default to current when not provided
            // or when the selected snapshot is the current snapshot.
            const originState: IGCDataLight = compareConfiguration?.leftSnapshotRef?.id
                ? await LoadGroupConfigurationDataAsync(
                    application.dataSource,
                    state,
                    compareConfiguration?.leftSnapshotRef?.id,
                    props.isMergeApp,
                )
                : loadBlankConfiguration();

            const targetState: IGCDataLight = compareConfiguration?.rightSnapshotRef?.id
                ? await LoadGroupConfigurationDataAsync(
                    application.dataSource,
                    state,
                    compareConfiguration?.rightSnapshotRef?.id,
                    props.isMergeApp,
                )
                : loadBlankConfiguration();

            const comparisonEngine = new ComparisonEngine(
                originState,
                targetState,
                {
                    compareConfiguration
                },
            );

            await comparisonEngine.processAsync();

            const compareViewModel = await ComparisonViewModelGenerator.GenerateGroupConfigurationModel(
                originState,
                targetState,
                comparisonEngine,
                targetState.config,
                compareConfiguration,
            );

            console.log('Comparison Engine:', comparisonEngine);
            console.log('Compare View Model:', compareViewModel);

            // if (props.isMergeApp) {
            //     const changeType = IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"];
            //     targetState.json.changeType = {
            //         type: 'optionset',
            //         name: changeType.name,
            //         value: changeType.value
            //     }
            // }

            // slight delay to ensure any loading states are shown before we set the new state
            setComparisonState({
                origin: originState,
                target: targetState,
                configuration: compareConfiguration,
            });

            setCompareViewModel(compareViewModel);
            setIsOpenCompareConfiguration(false);
            setCompareConfiguration(compareConfiguration);

            if (props.isUnderwritingReviewApp) {

                const commentsLookup: Record<string, IComment> = {};

                targetState.json.comments.forEach((eachComment: IComment) => {
                    let lastLocation = getLastLocation(eachComment);

                    // last node is of type valueRef?
                    if (lastLocation.type === ICommentRouteType.flexibleFieldValueRef) {
                        commentsLookup[lastLocation.id] = eachComment;
                    } else if (lastLocation.type === ICommentRouteType.root) {
                        commentsLookup['root'] = eachComment;
                    }
                });

                setCommentsLookup(commentsLookup);
                if (commentsLookup['root']) {
                    setCorrectionComment(commentsLookup['root'].comment);
                }

            }

            updateQueryParams(originState, targetState, compareConfiguration);

            return result;

        }
        , [
            application.dataSource,
            state,
        ]
    );

    const onConfirmationDialogContinue = (async () => {
 
        function setCommentRoot() {
            commentsLookup['root'] = structuredClone(commentsLookup['root']) ?? {
                id: uuidv4(),
                comment: '',
                status: ECommentStatus.New,
                modified: {
                    on: new Date().toISOString(),
                    by: {
                        id: authentication.principal?.systemUser?.systemuserid ?? '',
                        name: authentication.principal?.systemUser?.fullname ?? '',
                    }
                },
                created: {
                    on: new Date().toISOString(),
                    by: {
                        id: authentication.principal?.systemUser?.systemuserid ?? '',
                        name: authentication.principal?.systemUser?.fullname ?? '',
                    }
                },
                location: {
                    id: null as any,
                    name: 'Group Configuration',
                    type: ICommentRouteType.root,
                }
            };

            if (correctionComment) {
                commentsLookup['root'].comment = correctionComment;
            }
            else {
                delete commentsLookup['root'];
            }
        }

        // if target snapshot is not the same as the currently loaded state snapshot fail
        if (!comparisonState || !comparisonState.target || comparisonState.target.snapshot.bcbs_groupconfigurationsnapshotid !== state.data.snapshot.bcbs_groupconfigurationsnapshotid) {
            throw new Error('Target snapshot is not the same as the currently loaded state snapshot');
        }

        setCommentRoot();

        const comments: IComment[] = Object.values(commentsLookup);

        dispatch(
            SaveUnderwritingAction(
                application.dataSource,
                history,
                confirmationDialogState,
                structuredClone(comments),
            )
        );

        setConfirmationDialogState(EUnderwritingConfirmationDialogState.Hidden);
    });

    const onSetCommentInfo: TSetCommentInfoDelegate = useCallback(
        (comment, label, onOrigin, onTarget) => {
            setCommentInfo({
                comment,
                label,
                onOrigin,
                onTarget,
            });
        }, [state.data.account, state.data.snapshot]
    );

    const onCommentDialogCancel = useCallback(() => {
        setCommentInfo(null);
    }, []);

    const onToggleCommentResolved = useCallback((comment: IComment) => {
        setCommentsLookup(previous => {
            const key = Object.keys(previous).find(eachKey => previous[eachKey].id === comment.id);
            if (!key)
                return previous;

            return {
                ...previous,
                [key]: {
                    ...previous[key],
                    status: previous[key].status === ECommentStatus.Resolved ? ECommentStatus.New : ECommentStatus.Resolved,
                },
            };
        });
    }, []);

    const onCommentDialogAdd = useCallback(() => {

        if (!commentInfo || !commentInfo.comment)
            return;

        let lastLocation = getLastLocation(commentInfo.comment);

        setCommentsLookup(t => {
            // Edit flow: update only by exact id match.
            const existingKey = Object.keys(t).find(eachKey => t[eachKey]?.id === commentInfo.comment?.id);
            if (existingKey) {
                return {
                    ...t,
                    [existingKey]: commentInfo.comment!,
                };
            }

            // last node is of type valueRef?
            if (lastLocation.type === ICommentRouteType.flexibleFieldValueRef) {
                return {
                    ...t,
                    [lastLocation.id]: commentInfo.comment!,
                };
            }

            if (lastLocation.type === ICommentRouteType.root) {
                return {
                    ...t,
                    root: commentInfo.comment!,
                };
            }

            return t;
        });

        onCommentDialogCancel();
    }, [
        onCommentDialogCancel,
        dispatch,
        commentInfo,
    ]);

    const onEditComment = useCallback((comment: IComment) => {
        const lastLocation = getLastLocation(comment);
        const label = lastLocation?.name || lastLocation?.type || 'Comment';

        setCommentInfo({
            comment,
            label,
            onOrigin: true,
            onTarget: true,
        });
    }, []);

    const onConfirmationDialogCancel = useCallback(() => {
        setConfirmationDialogState(EUnderwritingConfirmationDialogState.Hidden);
    }, []);

    const renderCompareLookupDialog = () => {
        let variant = ECompareLookupDialogVariant.CompareAppDialog;

        if (props.isUnderwritingReviewApp) {
            variant = ECompareLookupDialogVariant.UnderwritingReviewDialog;
        }

        if (props.isMergeApp) {
            variant = ECompareLookupDialogVariant.MergeAppDialog;
        }

        return (
            <CompareLookupDialog
                variant={variant}
                onContinue={onCompareConfigurationDialogContinue}
                onDismiss={() => setIsOpenCompareConfiguration(false)}
                isOpen={isOpenCompareConfiguration}
                defaultOriginSnapshotId={routeParams.compareId}
            />
        );
    }

    const renderUnderwritingConfirmationDialog = () => (
        <UnderwritingConfirmationDialog
            state={confirmationDialogState}
            correctionComment={correctionComment}
            onCorrectionCommentChange={setCorrectionComment}
            onContinue={onConfirmationDialogContinue}
            onCancel={onConfirmationDialogCancel}
        />
    );

    const renderCommentDialog = () => (
        <AddCommentDialog
            commentInfo={commentInfo}
            onChangeComment={(comment) => setCommentInfo(t => ({
                ...t!,
                comment: {
                    ...t!.comment,
                    comment,
                } as any,
            }))}
            onAdd={onCommentDialogAdd}
            onCancel={onCommentDialogCancel}
        />
    );

    const renderModelWarning = () => (
        <ModelWarningMessage
            isUnderwritingReviewApp={props.isUnderwritingReviewApp}
            isCompareApp={props.isCompareApp}
            canUnderwritingReview={canUnderwritingReview}
            statuscode={state?.data?.snapshot?.statuscode}
            hasModel={!!compareViewModel}
        />
    );

    const renderPendingMergeWarning = () => (
        <PendingMergeWarning error={pendingMergeValidationError} />
    );

    const renderRootComment = () => {
        if (!props.isUnderwritingReviewApp) {
            return null;
        }

        if (!compareViewModel)
            return null;

        return (
            <div style={{ marginTop: '15px', marginBottom: '10px' }}>
                <CustomPanel
                    title="Comment"
                    styleType={EPanelStyleType.Default}
                    canCollapse={false}
                >
                    <Stack
                        tokens={{ childrenGap: 8 }}
                        styles={{ root: { margin: '10px' } }}
                    >
                        <TextField
                            label="Comment"
                            multiline
                            autoAdjustHeight
                            maxLength={4000}
                            value={correctionComment}
                            onChange={(event, newValue) => setCorrectionComment(newValue ?? "")}
                        />
                    </Stack>
                </CustomPanel>
            </div>
        );
    }

    useEffect(() => {
        if (!compareViewModel) {
            setPendingMergeValidationError(null);
        }
    }, [compareViewModel]);

    const renderComparison = () => {
        if (!compareConfiguration)
            return null;

        if (!compareViewModel)
            return null;

        return (
            <CompareListSection
                model={compareViewModel}
                configuration={compareConfiguration}
                onSetCommentInfo={onSetCommentInfo}
                markers={markers}
                isMergeApp={props.isMergeApp}
                isUnderwritingReviewApp={props.isUnderwritingReviewApp}
                comments={commentsLookup}
                isCommentReviewOpen={isOpenComments}
                onDismissCommentReview={() => setCommentsPanel(false)}
                onToggleCommentResolved={onToggleCommentResolved}
                onEditComment={onEditComment}
                isQuickNavOpen={isQuickNavOpen}
                onDismissQuickNav={() => setQuickNavOpen(false)}
            />
        );
    }

    const renderFloatingActions = () => {
        if (!compareConfiguration)
            return null;

        if (!compareViewModel)
            return null;

        return (
            <CompareFloatingActionButtons
                model={compareViewModel}
                configuration={compareConfiguration}
                markers={markers}
                onSetIsQuickNavOpen={setQuickNavOpen}
            />
        );
    }

    return (
        <GroupConfigurationSnapshotContext
            contextMenu={contextMenu}
            hideShowDeactivated={true}
            hideSave={true}
            unStickyNavbar={true}
            isMergeApp={props.isMergeApp}
            isCompareApp={props.isCompareApp}
            isUnderwritingReviewApp={props.isUnderwritingReviewApp}
        >
            {renderUnderwritingConfirmationDialog()}
            {renderModelWarning()}
            {renderCommentDialog()}
            {renderCompareLookupDialog()}
            {renderRootComment()}
            {renderFloatingActions()}
            {renderPendingMergeWarning()}
            {renderComparison()}
        </GroupConfigurationSnapshotContext>
    );
};

export default CompareContext;
